import { Component, Input, OnChanges, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import moment from 'moment';
import { ThemeService } from '@wind-log_library/fe-component-library';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { ForecastWeatherProviderDataModel } from '../../services-api-v2/forecast/data/forecast-data.model';
import { ScheduleEventsModel } from '../../services-api-v2/planning/availability-declarations/schedule/schedule-events.model';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { HelperService } from 'app/shared/services/helper-service/helper.service';
import { Suggestion } from 'app/shared/services-api-v2/planning/suggestions/suggestion';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import Highcharts from 'highcharts';
import { CS_AREA_CHART_OPTIONS } from 'app/shared/helper/highchart-options';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';
import { getXDateFormat } from 'app/shared/helper/highcharts.helper';

export interface ForecastChartData {
  actualData: number[];
  ratedPower: number;
  forecastData: ForecastWeatherProviderDataModel[];
}

@Component({
  selector: 'app-forecast-chart',
  templateUrl: './forecast-chart.component.html',
  styleUrls: ['./forecast-chart.component.scss'],
  standalone: false,
})
export class ForecastChartComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('container', { static: true }) container: ElementRef;

  @Input() forecastData: ForecastWeatherProviderDataModel[];
  @Input() actualData: number[];
  @Input() days: number[];
  @Input() from: number;
  @Input() to: number;
  @Input() showWeatherTable = 'none'; // none | full | small
  @Input() showRevenues = false;
  @Input() ratedPower: number;
  @Input() fromReport: boolean; // Only if the component is called from report
  @Input() fromPlanning: boolean = false; // Only if the component is called from planning
  @Input() scheduleEventsData: ScheduleEventsModel; // in planning module we also show planned events in this chart
  @Input() isPlanningModule: boolean = false;
  @Input() suggestEventsData: Array<Suggestion>;
  @Input() timezone: string; // timezone of the report, if not set, then use browser timezone
  @Input() reportHeight: number;

  public plugins: any = [];
  public unit = 'MW';

  public forecastDataProcessed: ForecastWeatherProviderDataModel;

  public data = [];
  public chartLabels = [];
  public tooltipLabels = [];
  public forecastColors = [];

  private image = new Image();
  private unsubscriber = new ReplaySubject();

  private smallMode = false;

  public chartOptions: Highcharts.Options;
  public highcharts = Highcharts;
  public chartRef: Highcharts.Chart | null = null;

  private suggestedEventsRowGroup?: Highcharts.SVGElement;
  private suggestedEventsRow?: Highcharts.SVGElement;
  private chartHeight: number = 280;

  @ViewChild('powerChart', { static: true }) powerChart: ElementRef;

  constructor(
    private themeService: ThemeService,
    private translate: TranslateService,
    private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
    private dateTimeFormatService: DateTimeFormatService,
    private helper: HelperService,
    public timezoneService: TimezoneService,
  ) {}

  @HostListener('window:resize')
  onResize() {
    this.smallMode = this.container.nativeElement.offsetWidth < 650;
    this.setChartOptions();
    this.chartLabels = this.generateChartLabels();
  }

  private generateChartLabels(): any[] {
    return this.helper.generateChartDateLabels(
      this.timezoneService.unApplyTzFromDate(this.from, this.timezone).valueOf(),
      this.timezoneService.unApplyTzFromDate(this.to, this.timezone).valueOf(),
      this.powerChart,
      this.forecastData.filter(d => d.theoreticalPower.length > 0).length > 0
        ? this.forecastData.filter(d => d.theoreticalPower.length > 0)[0].theoreticalPower.length
        : 0,
    );
  }

  ngOnInit() {
    this.subscribeLanguageChange();
    this.subscribeOnThemeChanges();
    this.image.src = 'assets/monitor/portfolio/power-forecast/non-working-hours-icon-light.svg';
  }

  ngOnChanges() {
    if (this.days) {
      if (this.forecastData.length === 1) {
        this.forecastDataProcessed = this.forecastData[0];
      } else if (this.forecastData.length > 1) {
        // take average of all selected weather providers:
        this.forecastDataProcessed = new ForecastWeatherProviderDataModel({
          weather: [],
          temperature: [],
          windSpeed: [],
          windDirection: [],
          theoreticalPower: [],
          theoreticalPowerAmended: [],
          negativeImbalance: [],
          positiveImbalance: [],
          revenueLosses: [],
        });
        if (this.forecastData[0]) {
          for (let i = 0; i < this.forecastData[0].temperature.length; i++) {
            this.calcAverageForField('temperature', i);
            this.calcAverageForField('windSpeed', i);
            this.calcAverageForField('windDirection', i);
            this.calcAverageForField('revenueLosses', i);
          }
        }
      }
      this.setChartColors();
      this.setChartData();
    }
  }

  public rotate(value) {
    return { transform: 'rotate(' + (+value + 180) + 'deg)' };
  }

  private setChartData() {
    if (this.forecastData.length === 0) {
      return;
    }
    this.forecastData = this.forecastData.filter(f => f);
    const actualDataProcessed = [];
    this.chartLabels = this.generateChartLabels();
    this.tooltipLabels = [];
    this.data = [];
    const stepSize =
      (this.to - this.from + 1) /
      (this.forecastData.filter(d => d.theoreticalPower?.length > 0).length > 0
        ? this.forecastData.filter(d => d.theoreticalPower?.length > 0)[0].theoreticalPower?.length
        : 0);
    let tempTs = this.from;
    let index = 0;
    const now = moment().unix() * 1000;
    while (tempTs < this.to) {
      this.tooltipLabels.push(this.timezoneService.unApplyTzFromDate(tempTs, this.timezone).valueOf());
      // we need to change 0 to null for all actual values *after* current timestamp
      if (tempTs <= now) {
        actualDataProcessed.push(this.actualData[index] / (this.days.length === 1 ? 1 : 1));
      } else {
        actualDataProcessed.push(null);
      }
      tempTs = Math.ceil(tempTs + stepSize);
      index++;
    }

    this.forecastData.filter(f => f.name === 'Wind-Log').map(f => (f.name = 'GPM Horizon'));
    if (this.forecastData.length === 1) {
      this.data = [
        // attention: order is important!
        {
          id: 1,
          label: this.translate.instant('PLANNING.PARK.ACTUAL'),
          color: this.themeService.getColor('--primary-background'),
          data: actualDataProcessed,
        },
        {
          id: 2,
          label: this.forecastData[0].name + ' ' + this.translate.instant('PLANNING.PARK.THEO_ABBREV'),
          color: this.themeService.getColor('--chart-border-color-theo-amended'),
          data: this.forecastData[0].theoreticalPowerAmended.map(v => (v ? v / 1000000 : 0)),
        },
        {
          id: 3,
          label: `${this.translate.instant('PLANNING.PARK.PROD_LOSS_CHART')}${this.fromPlanning ? ' (Planning)' : ''}`,
          color: this.themeService.getColor('--chart-border-color-theo'),
          data: this.forecastData[0].theoreticalPower.map(v => (v ? v / 1000000 : 0)),
        },
      ];
    } else {
      // multiple forecast providers:
      this.data = [
        // attention: order is important!
        {
          id: 1,
          label: this.translate.instant('PLANNING.PARK.ACTUAL'),
          color: this.themeService.getColor('--primary-background'),
          data: actualDataProcessed,
        },
        {
          id: 2,
          label: this.forecastData[0]?.name + ' ' + this.translate.instant('PLANNING.PARK.THEO_ABBREV'),
          color: this.getColor(this.forecastData[0]?.name),
          data: this.forecastData[0]?.theoreticalPowerAmended.map(v => (v ? v / 1000000 : 0)),
        },
        {
          id: 3,
          label:
            (this.forecastData.length > 1 ? this.forecastData[1].name + ' ' : '') +
            this.translate.instant('PLANNING.PARK.THEO_ABBREV'),
          color: this.forecastData.length > 1 ? this.getColor(this.forecastData[1].name) : null,
          data:
            this.forecastData.length > 1
              ? this.forecastData[1].theoreticalPowerAmended?.map(v => (v ? v / 1000000 : 0))
              : null,
        },
        {
          id: 4,
          label:
            (this.forecastData.length > 2 ? this.forecastData[2].name + ' ' : '') +
            this.translate.instant('PLANNING.PARK.THEO_ABBREV'),
          color: this.forecastData.length > 2 ? this.getColor(this.forecastData[2].name) : null,
          data:
            this.forecastData.length > 2
              ? this.forecastData[2].theoreticalPowerAmended?.map(v => (v ? v / 1000000 : 0))
              : null,
        },
        {
          id: 5,
          label:
            (this.forecastData.length > 3 ? this.forecastData[3].name + ' ' : '') +
            this.translate.instant('PLANNING.PARK.THEO_ABBREV'),
          color: this.forecastData.length > 3 ? this.getColor(this.forecastData[3].name) : null,
          data:
            this.forecastData.length > 3
              ? this.forecastData[3].theoreticalPowerAmended?.map(v => (v ? v / 1000000 : 0))
              : null,
        },
        {
          id: 6,
          label:
            (this.forecastData.length > 4 ? this.forecastData[4].name + ' ' : '') +
            this.translate.instant('PLANNING.PARK.THEO_ABBREV'),
          color: this.forecastData.length > 4 ? this.getColor(this.forecastData[4].name) : null,
          data:
            this.forecastData.length > 4
              ? this.forecastData[4].theoreticalPowerAmended?.map(v => (v ? v / 1000000 : 0))
              : null,
        },
      ].filter(a => a.data);
    }
    if (this.ratedPower) {
      this.data.push({
        id: 1000,
        label: this.translate.instant('COMMON.CHART_LEGEND_RATED_POWER'),
        color: '#aaaaaa',
        data: new Array(this.tooltipLabels.length).fill(this.ratedPower / 1000000),
      });
    }
    /* Hidden serie for the non-working hours legend */
    this.data.push({
      id: 1001,
      label: this.translate.instant('POWER_FORECAST.NON_WORKING'),
      color: 'transparent',
      data: [],
    });
    this.setChartOptions();
  }

  private getColor(name: string): string {
    switch (name) {
      case 'GPM Horizon':
        return this.themeService.getColor('--forecast-provider-horizon');
      case 'BKW Trading':
        return this.themeService.getColor('--chart-bg-color-orange');
      case 'Open Weather':
        return this.themeService.getColor('--forecast-provider-open-weather');
      case 'Meteo Blue':
        return this.themeService.getColor('--forecast-provider-meteo-blue');
      case 'Meteologica':
        return this.themeService.getColor('--forecast-provider-meteologica');
      case 'DNV Forecaster':
        return this.themeService.getColor('--forecast-provider-dnv-forecaster');
    }
  }

  private timestampIsNonWorking(timestamp: number): boolean {
    const dt = moment(Number(timestamp));
    const hour = dt.hours();
    const day = dt.day();
    const isWeekend = day === 6 || day === 0;
    return isWeekend || hour < 8 || hour > 17;
  }

  public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

  private setChartOptions() {
    const self = this;
    const series = this.data
      .map((a, i) => ({
        serieId: a.id,
        type: a.id === 1000 || a.id === 1 ? 'line' : 'area',
        name: a.label,
        color: a.color,
        lineWidth: a.id === 1000 ? 1.5 : 2,
        fillColor: a.id === 1000 || a.id === 1001 ? 'transparent' : this.forecastColors[i].backgroundColor,
        dashStyle: a.id === 1000 ? 'Dash' : 'Solid',
        yAxis: 0,
        order: a.id,
        units: 'MW',
        visible: true,
        showInLegend: true,
        data: a?.data?.map((v, i) => [this.timezoneService.unApplyTzFromDate(this.tooltipLabels[i]).valueOf(), v]),
      }))
      .sort((a, b) => a.serieId - b.serieId);
    const yAxisMax = this.ratedPower
      ? (this.ratedPower / 1000000) * 1.1
      : Math.max(...this.actualData, Math.max(...this.forecastData[0].theoreticalPowerAmended) / 1000000) * 1.1;
    const chartAxis = this.chartAxisConfiguration(yAxisMax);
    this.chartOptions = Highcharts.merge(CS_AREA_CHART_OPTIONS, {
      chart: {
        height: this.fromReport ? (this.reportHeight ?? this.chartHeight) : this.chartHeight,
        events: {
          render: function () {
            self.suggestEventsData && self.suggestEventsData.length > 0 ? self.dragSuggestedEventsLines() : null;
          },
        },
      },
      plotOptions: {
        series: {
          animation: { duration: 0 },
          marker: {
            enabled: true,
            radius: 0.1,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
                radius: 5,
              },
            },
          },
          states: {
            hover: {
              enabled: true,
            },
            inactive: { enabled: false },
          },
        },
      },
      legend: this.chartLegendConfiguration(),
      xAxis: chartAxis.xAxis,
      yAxis: chartAxis.yAxis,
      tooltip: {
        enabled: true,
        outside: true,
        followPointer: true,
        hideDelay: 0,
        shared: true,
        animation: { duration: 0 },
        backgroundColor: 'var(--card)',
        style: {
          color: 'var(--primary)',
        },
        useHTML: true,
        formatter: function () {
          const date = self.dateTimeFormatPreferencePipe.transform(
            moment(this.points[0].x).toDate(),
            'dmyt',
            this.timezone,
          );
          let tooltipContent = `<div class="font-weight-bold" style="margin-bottom: 5px; font-size: 14px">${date}</div>`;
          this.points.reverse().forEach(p => {
            tooltipContent += `
              <div style="margin-bottom: 2px; font-size: 12px">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50px; border: 2px solid ${p?.color}; background-color: ${p.color}"></span>
                ${p?.series?.userOptions?.name}: <b>${p.y.toFixed(2)} ${p?.series?.userOptions?.units}</b>
              </div>
            `;
          });
          return tooltipContent;
        },
      },
      series: series.reverse(),
    });

    setTimeout(() => {
      this.chartRef?.update(this.chartOptions, true, true);
    }, 10);
  }

  private getColorForCategory(category: string) {
    switch (category) {
      case 'Maintenance':
        return this.themeService.getColor('--event-category-maintainance') + '55';
      case 'Repairs':
        return this.themeService.getColor('--event-category-repairs') + '55';
      case 'Grid Limitation':
        return this.themeService.getColor('--event-category-grid-lim') + '55';
      case 'Environmental':
        return this.themeService.getColor('--event-category-environmental') + '55';
      case 'Inspection':
        return this.themeService.getColor('--event-category-inspection') + '55';
      default:
        return this.themeService.getColor('--black');
    }
  }

  private chartAxisConfiguration(yAxisMax: number) {
    const xAxis = {
      type: 'datetime',
      tickPixelInterval: 60,
      plotBands: this.getPlotBands(),
      minPadding: 0,
      maxPadding: 0,
      tickColor: 'var(--divider)',
      lineWidth: 1,
      lineColor: 'var(--divider)',
      gridLineWidth: 1,
      gridLineColor: 'var(--divider)',
      plotLines: this.nowLineAnnotation(),
      dateTimeLabelFormats: {
        day: getXDateFormat('dm', this.dateTimeFormatService),
        hour: getXDateFormat('t', this.dateTimeFormatService),
        minute: getXDateFormat('t', this.dateTimeFormatService),
      },
    };

    const yAxis = [
      {
        tickAmount: 6,
        lineWidth: 1,
        opposite: false,
        tickColor: 'var(--font)',
        gridLineColor: 'var(--divider)',
        lineColor: 'var(--divider)',
        min: 0,
        max: yAxisMax,
        title: {
          text: 'MW',
          style: {
            color: 'var(--font)',
            fontFamily: 'SF Display',
            fontSize: '12px',
          },
        },
        labels: {
          style: {
            color: 'var(--font)',
            fontFamily: 'SF Display',
            fontSize: '12px',
          },
          formatter: function () {
            return this.value;
          },
        },
      },
    ].filter(a => a);
    return { xAxis, yAxis };
  }

  private chartLegendConfiguration() {
    return {
      align: 'center',
      verticalAlign: 'top',
      layout: 'horizontal',
      reversed: true,
      padding: 5,
      itemStyle: {
        color: 'var(--font)',
        fontSize: '12px',
      },
      zIndex: 1,
      enabled: true,
      useHTML: true,
      symbolWidth: 0,
      symbolHeight: 0,
      labelFormatter: function () {
        const params = this as any;
        const seriesColor = params.userOptions.lineColor || params.userOptions.color;
        const backgroundColor = params.userOptions.fillColor;
        const seriesName = params.name;
        if (params.userOptions.serieId === 1001) {
          const patternBox = `
            <span style="display: inline-flex; align-items: center; margin-right: 6px;">
              <svg width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="legendPattern" patternUnits="userSpaceOnUse" width="4" height="4">
                    <rect width="6" height="6" fill="var(--card)"/>
                    <path d="M 0 0 L 6 6 M 5 -1 L 7 1"
                          stroke="var(--divider)"
                          stroke-width="1"/>
                  </pattern>
                </defs>

                <rect width="14" height="14"
                      fill="url(#legendPattern)"
                      stroke="var(--divider)"
                      stroke-width="1"/>
              </svg>
            </span>
          `;
          return `
            <div style="display: flex; align-items: center">
              ${patternBox}
              ${seriesName}
            </div>
          `;
        } else {
          const markerHtml = `<span style="display: inline-block;
            width: 14px; height: 14px;
            border: 2px solid ${seriesColor};
            border-radius: 50%;
            background-color: ${backgroundColor};
            margin-right: 5px;
            vertical-align: middle;"></span>`;
          return `<div style="display: flex; align-items: center">${markerHtml} ${seriesName}</div>`;
        }
      },
    };
  }

  private nowLineAnnotation() {
    return [
      {
        color: 'var(--primary-background)',
        width: 2,
        value: Date.now(),
        zIndex: 99,
        label: {
          text: this.translate.instant('FORECAST.CHART.NOW'),
          align: 'left',
          rotation: 0,
          y: 5,
          x: -20,
          useHTML: true,
          style: {
            color: 'var(--primary-opposite)',
            fontWeight: 'bold',
          },
          formatter: function () {
            return `<div style="
            background-color: var(--primary-background);
            color: #fff;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 12px;
            z-index: -1;
            box-shadow: 0 1px 4px rgba(0,0,0,0.2);
          ">Now</div>`;
          },
        },
      },
    ];
  }

  private setChartColors(): void {
    if (this.forecastData.length === 1) {
      this.forecastColors = [
        {
          // Actual
          backgroundColor: 'transparent',
          borderColor: this.themeService.getColor('--primary-background'),
          borderWidth: 1,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 2,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#aaa',
        },
        {
          // Theo Amended
          backgroundColor: this.themeService.getColor('--chart-bg-color-theo-amended'),
          borderColor: this.themeService.getColor('--chart-border-color-theo-amended'),
          borderWidth: 2,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 2,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#aaa',
        },
        {
          // Theo
          backgroundColor: this.themeService.getColor('--chart-bg-color-theo'),
          borderColor: this.themeService.getColor('--chart-border-color-theo'),
          borderWidth: 1,
          fill: true,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 2,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#aaa',
        },
        {
          // Rated
          backgroundColor: null,
          borderColor: '#aaaaaa',
          borderWidth: 1,
          fill: false,
          borderDash: [5, 5],
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 0,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#aaa',
        },
      ];
    } else {
      // multiple forecast providers
      this.forecastColors = [
        {
          // Actual
          backgroundColor: 'transparent',
          borderColor: this.themeService.getColor('--primary-background'),
          borderWidth: 1,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 1,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#aaa',
        },
        {
          backgroundColor: 'transparent',
          borderColor: this.forecastData[0] ? this.getColor(this.forecastData[0].name) : null,
          borderWidth: 2,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 2,
          fill: '-1',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(220,220,220,1)',
        },
        {
          backgroundColor: 'transparent',
          borderColor: this.forecastData[1] ? this.getColor(this.forecastData[1].name) : null,
          borderWidth: 2,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 2,
          fill: '-1',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(220,220,220,1)',
        },
        {
          backgroundColor: 'transparent',
          borderColor: this.forecastData[2] ? this.getColor(this.forecastData[2].name) : null,
          borderWidth: 2,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 2,
          fill: '-1',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(220,220,220,1)',
        },
        {
          backgroundColor: 'transparent',
          borderColor: this.forecastData[3] ? this.getColor(this.forecastData[3].name) : null,
          borderWidth: 2,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 2,
          fill: '-1',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(220,220,220,1)',
        },
        {
          // Rated
          backgroundColor: null,
          borderColor: '#aaaaaa',
          borderWidth: 1,
          fill: false,
          borderDash: [5, 5],
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 0,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#aaa',
        },
      ];
    }
  }

  private calcAverageForField(field: string, i: number) {
    let num = 0;
    let validValuesCount = 0;
    for (let i2 = 0; i2 < this.forecastData.length; i2++) {
      if (this.forecastData[i2] && this.forecastData[i2][field][i]) {
        validValuesCount += 1;
        num += this.forecastData[i2][field][i];
      }
    }
    this.forecastDataProcessed[field].push(num / validValuesCount);
  }

  private getPlotBands = (): Highcharts.XAxisPlotBandsOptions[] => {
    const timeInterval = 1000 * 60 * 60;
    const allTs = this.tooltipLabels.map(t => this.timezoneService.unApplyTzFromDate(t).valueOf());
    const timestamps = [allTs[0]];
    let allPlotbands = [];
    for (let i = 0; i < allTs.length - 1; i++) {
      if (
        (this.timestampIsNonWorking(allTs[i]) && !this.timestampIsNonWorking(allTs[i + 1])) ||
        (this.timestampIsNonWorking(allTs[i + 1]) && !this.timestampIsNonWorking(allTs[i]))
      ) {
        timestamps.push(allTs[i]);
      }
    }
    const nonWorkingPlotbands = timestamps
      .map((ts, index) =>
        !this.timestampIsNonWorking(ts + timeInterval)
          ? null
          : {
              id: `nonWorkingPlot-${ts}`,
              from: ts,
              to: timestamps[index + 1] || allTs[allTs.length - 1],
              color: {
                pattern: {
                  color: 'var(--divider)',
                  path: 'M 0 0 L 10 10 M 9 -1 L 11 1',
                  width: 10,
                  height: 10,
                },
              },
            },
      )
      .filter(band => !!band);
    allPlotbands.push(...nonWorkingPlotbands);

    if (this.scheduleEventsData) {
      const scheduleEventsPlotbands = this.scheduleEventsData.allEvents.map((a, i) => ({
        id: `scheduledEventPlotband-${i}`,
        from: a.startTs,
        to: a.endTs,
        color: this.getColorForCategory(a.category),
        borderColor: this.getColorForCategory(a.category),
        zIndex: 10,
      }));
      allPlotbands.push(...scheduleEventsPlotbands);
    }
    return allPlotbands;
  };

  private dragSuggestedEventsLines() {
    const chart = this.chartRef;
    const xAxis = chart.xAxis[0];
    if (!xAxis) return;

    const suggestedEventsRowHeight = 8;
    const y = chart.plotTop + chart.plotHeight + 3;
    const left = chart.plotLeft;
    const right = chart.plotLeft + chart.plotWidth;
    const width = chart.plotWidth;

    if (!this.suggestedEventsRowGroup) {
      this.suggestedEventsRowGroup = chart.renderer.g('status-strips').attr({ zIndex: 3 }).add();
      (this.suggestedEventsRowGroup.element as any).style.pointerEvents = 'none';
    }

    if (!this.suggestedEventsRow) {
      this.suggestedEventsRow = chart.renderer.clipRect(left, y, width, suggestedEventsRowHeight).add();
    } else {
      (this.suggestedEventsRow as any).attr({ x: left, y, width, height: suggestedEventsRowHeight });
    }
    this.suggestedEventsRowGroup.clip(this.suggestedEventsRow as any);

    while (this.suggestedEventsRowGroup.element.childNodes.length) {
      this.suggestedEventsRowGroup.element.removeChild(this.suggestedEventsRowGroup.element.firstChild!);
    }

    for (const event of this.suggestEventsData) {
      const start = Math.max(event.start, xAxis.min ?? event.start);
      const end = Math.min(event.end, xAxis.max ?? event.end);
      if (start >= end) continue;

      const x1 = xAxis.toPixels(start, false);
      const x2 = xAxis.toPixels(end, false);

      const rectX = Math.max(left, x1);
      const rectW = Math.min(right, x2) - rectX;
      if (rectW <= 0) continue;
      chart.renderer
        .rect(rectX, y, rectW, suggestedEventsRowHeight, 4)
        .attr({
          fill: event.color,
          stroke: event.color,
          'stroke-width': 0,
          opacity: 1,
        })
        .add(this.suggestedEventsRowGroup);
    }
  }

  ngOnDestroy() {
    this.suggestedEventsRowGroup?.destroy();
    this.suggestedEventsRow?.destroy();
    this.suggestedEventsRowGroup = undefined;
    this.suggestedEventsRow = undefined;
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  private subscribeLanguageChange() {
    moment.locale(this.translate.currentLang);
    this.translate.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
      moment.locale(event.lang);
      this.setChartData();
    });
  }

  private subscribeOnThemeChanges(): void {
    this.themeService
      .getTheme()
      .pipe(takeUntil(this.unsubscriber), skip(1))
      .subscribe(() => {
        this.setChartColors();
        this.setChartData();
      });
  }
}
